import type { SocCatalogItem, SocTenantOption } from '@/api/soc'

/**
 * Texto da área de SOC. Separado do componente porque é a parte que carrega
 * decisão — o que a tela AFIRMA — e a parte que precisa de teste.
 */

/** `SOC - <tenant>-<AMBIENTE>`. O ambiente vem de `tenants.environment`, então
 *  o sufixo é derivado, nunca digitado. */
export function nomeDerivado(t: Pick<SocTenantOption, 'name' | 'environment'>): string {
  return `SOC - ${t.name}-${t.environment.toUpperCase()}`
}

/**
 * O rótulo do nível diz o PAPEL, não só o número: "Nível 2" sozinho não informa
 * que ele só entra se ninguém reconhecer.
 */
export function papelDoNivel(nivel: 1 | 2 | 3): string {
  if (nivel === 1) return 'recebe o alarme primeiro'
  if (nivel === 2) return 'recebe se ninguém reconhecer'
  return 'última escala'
}

export function rotuloDoNivel(nivel: 1 | 2 | 3): string {
  return `Nível ${nivel} — ${papelDoNivel(nivel)}`
}

/**
 * O contador é da FONTE, não deste alerta: a agregação do backend é por
 * `source`, porque contar por grupo de regra exigiria varrer JSONB não indexado
 * numa tabela particionada. Dizer "disparos deste alerta" seria número
 * verossímil e errado — e é justamente a forma de slop que mais engana.
 */
export function rotuloHistorico(i: Pick<SocCatalogItem, 'hits_da_fonte' | 'fonte_nunca_entregou'>): string {
  if (i.fonte_nunca_entregou) return 'Esta fonte nunca entregou nada'
  const n = i.hits_da_fonte
  return `${n} ${n === 1 ? 'evento' : 'eventos'} desta fonte em 90 dias`
}

/**
 * Estado do reconhecimento, para o chat de alertas. Tem de responder "alguém
 * pegou isso?" — "pendente" não responde.
 */
export function formatEstadoAck(e: {
  reconhecido: boolean
  acked_by: string | null
  current_level: number
  minutos_decorridos: number
}): string {
  if (e.reconhecido) return `Reconhecido por ${e.acked_by}`
  if (e.minutos_decorridos < 1) return `Não reconhecido — nível ${e.current_level}`
  return `Não reconhecido há ${e.minutos_decorridos} min — nível ${e.current_level}`
}

/** Minutos entre `desde` e agora, nunca negativo (relógio do cliente adiantado
 *  produziria "há -3 min", que lê como defeito). */
export function minutosDesde(desde: string, agora: Date = new Date()): number {
  const t = Date.parse(desde)
  if (Number.isNaN(t)) return 0
  return Math.max(0, Math.floor((agora.getTime() - t) / 60000))
}
